import { useEffect, useRef, useState } from 'react';
import { EventCategory, InsightEvent } from '../../types/insights';
import { MarkerShape } from './UPlotChart';

// ---------------------------------------------------------------------------
// EventMarkerRow — DOM glyph row on the shared time axis (free click targets,
// unlike canvas markers). Glyphs are pixel-bucket clustered into 14 px bins;
// a bin with more than one event renders the highest-severity glyph plus a
// ×N badge, and clicking it opens a popover list to pick the exact event.
// ---------------------------------------------------------------------------

/** Category → color/glyph/shape/label. Colorblind-safe via shape redundancy. */
export const CATEGORY_STYLE: Record<
  EventCategory,
  { color: string; glyph: string; shape: MarkerShape; label: string }
> = {
  disconnect:         { color: '#c0392b', glyph: '▲', shape: 'triangle',     label: 'Disconnect' },
  gap:                { color: '#8a97a6', glyph: '▲', shape: 'triangle',     label: 'Telemetry gap' },
  screendoor_suspect: { color: '#d97706', glyph: '▼', shape: 'triangleDown', label: 'Screendoor suspect' },
  render_degraded:    { color: '#b3468f', glyph: '▼', shape: 'triangleDown', label: 'Render degraded' },
  operator_issue:     { color: '#b47d0e', glyph: '●', shape: 'circle',       label: 'Operator mark' },
  freeze:             { color: '#6d3fa8', glyph: '■', shape: 'square',       label: 'Freeze' },
  crash:              { color: '#16202e', glyph: '✕', shape: 'cross',        label: 'Crash' },
  router_settings:    { color: '#2B6AC0', glyph: '◆', shape: 'diamond',      label: 'Router settings' },
  channel_change:     { color: '#0e7f7f', glyph: '◇', shape: 'diamondOpen',  label: 'Channel change' },
  router_login:       { color: '#607086', glyph: '○', shape: 'circleOpen',   label: 'Router login' },
  wifi:               { color: '#9aa7b4', glyph: '•', shape: 'dot',          label: 'WiFi (raw)' },
  test_run:           { color: '#8a97a6', glyph: '▦', shape: 'square',       label: 'Test run' },
  other:              { color: '#8a97a6', glyph: '●', shape: 'circle',       label: 'Other' },
};

const SEVERITY_RANK: Record<string, number> = { error: 3, warning: 2, info: 1 };

const BIN_PX = 14;

interface Cluster {
  px: number;
  events: InsightEvent[];
  top: InsightEvent; // highest severity, earliest wins ties
}

interface EventMarkerRowProps {
  events: InsightEvent[];
  range: [number, number];
  onSelect: (event: InsightEvent) => void;
  selectedEventId?: string | null;
}

const fmtTime = (ts: number) =>
  new Date(ts * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

export default function EventMarkerRow({ events, range, onSelect, selectedEventId }: EventMarkerRowProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  const [openCluster, setOpenCluster] = useState<Cluster | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Close the popover on any outside click.
  useEffect(() => {
    if (!openCluster) return;
    const close = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpenCluster(null);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openCluster]);

  const [from, to] = range;
  const span = Math.max(to - from, 1);

  // Pixel-bucket clustering.
  const clusters: Cluster[] = [];
  if (width > 0) {
    const bins = new Map<number, InsightEvent[]>();
    for (const ev of events) {
      if (ev.ts < from || ev.ts > to) continue;
      const px = ((ev.ts - from) / span) * width;
      const bin = Math.floor(px / BIN_PX);
      const list = bins.get(bin);
      if (list) list.push(ev);
      else bins.set(bin, [ev]);
    }
    for (const [bin, list] of bins) {
      list.sort((a, b) => a.ts - b.ts);
      const top = [...list].sort(
        (a, b) => (SEVERITY_RANK[b.severity] ?? 0) - (SEVERITY_RANK[a.severity] ?? 0) || a.ts - b.ts
      )[0];
      clusters.push({ px: bin * BIN_PX + BIN_PX / 2, events: list, top });
    }
    clusters.sort((a, b) => a.px - b.px);
  }

  const handleClick = (c: Cluster) => {
    if (c.events.length === 1) {
      setOpenCluster(null);
      onSelect(c.events[0]);
    } else {
      setOpenCluster((prev) => (prev === c ? null : c));
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', height: 18 }}>
      {clusters.map((c, i) => {
        const style = CATEGORY_STYLE[c.top.category];
        const isSelected = c.events.some((e) => e.id === selectedEventId);
        return (
          <span
            key={i}
            onClick={() => handleClick(c)}
            title={
              c.events.length === 1
                ? `${fmtTime(c.top.ts)} · ${c.top.title}`
                : `${c.events.length} events — click to list`
            }
            style={{
              position: 'absolute',
              left: c.px,
              top: 0,
              transform: 'translateX(-50%)',
              cursor: 'pointer',
              fontSize: 11,
              lineHeight: '18px',
              color: style.color,
              userSelect: 'none',
              padding: '0 2px',
              borderRadius: 4,
              background: isSelected ? 'rgba(43,106,192,0.18)' : 'transparent',
              whiteSpace: 'nowrap',
            }}
          >
            {style.glyph}
            {c.events.length > 1 && (
              <sup style={{ fontSize: 9, fontWeight: 700, color: '#54626f', marginLeft: 1 }}>
                ×{c.events.length}
              </sup>
            )}
          </span>
        );
      })}

      {openCluster && (
        <div
          style={{
            position: 'absolute',
            left: Math.min(Math.max(openCluster.px, 90), Math.max(width - 130, 90)),
            top: 20,
            transform: 'translateX(-50%)',
            zIndex: 120,
            background: '#fff',
            border: '1px solid #d5dce5',
            borderRadius: 10,
            boxShadow: '0 8px 28px rgba(16,32,46,0.22)',
            minWidth: 240,
            maxWidth: 340,
            maxHeight: 220,
            overflowY: 'auto',
            padding: 6,
          }}
        >
          {openCluster.events.map((ev) => {
            const st = CATEGORY_STYLE[ev.category];
            return (
              <div
                key={ev.id}
                onClick={() => {
                  setOpenCluster(null);
                  onSelect(ev);
                }}
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'baseline',
                  padding: '5px 8px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 12,
                  background: ev.id === selectedEventId ? '#EEF4FC' : 'transparent',
                }}
                onMouseEnter={(e) => ((e.currentTarget as HTMLDivElement).style.background = '#f3f6fa')}
                onMouseLeave={(e) =>
                  ((e.currentTarget as HTMLDivElement).style.background =
                    ev.id === selectedEventId ? '#EEF4FC' : 'transparent')
                }
              >
                <span style={{ color: st.color, flexShrink: 0 }}>{st.glyph}</span>
                <span style={{ color: '#54626f', fontVariant: 'tabular-nums', flexShrink: 0 }}>
                  {fmtTime(ev.ts)}
                </span>
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: '#16202e',
                  }}
                  title={ev.title}
                >
                  {ev.title}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
